import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { AdminOverview } from "./admin.overview";
import { DepartmentsPage } from "./admin.departments";
import { UsersPage } from "./admin.users";
import { ModeratorsPage } from "./admin.moderators";
import { AuditLogPage } from "./admin.audit-log";
import { ModeratorAuditPage } from "./admin.moderator-audit";
import { TeamActivityPage } from "../_team/team.activity";
import { AdsSettingsPage } from "./admin.settings.ads";
import { ColorThemePage } from "../settings_.color-theme";
import { IntegrationsSettingsPage } from "./admin.settings.integrations";

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "departments", label: "Departments" },
  { value: "users", label: "Users" },
  { value: "moderators", label: "Moderators" },
  { value: "audit-log", label: "Audit Log" },
  { value: "moderator-audit", label: "Moderator Audit" },
  { value: "activity-feed", label: "Activity Feed" },
  { value: "ads", label: "Ads Performance" },
  { value: "integrations", label: "Integrations" },
  { value: "color-theme", label: "Color Theme" },
] as const;

type TabValue = (typeof TABS)[number]["value"];

const searchSchema = z.object({
  tab: z
    .enum(["overview", "departments", "users", "moderators", "audit-log", "moderator-audit", "activity-feed", "ads", "integrations", "color-theme"])
    .default("overview")
    .catch("overview"),
  dept: z.string().optional(),
});

export const Route = createFileRoute("/_app/_admin/admin/settings")({
  validateSearch: (s) => searchSchema.parse(s),
  component: SettingsTabsPage,
});

function SettingsTabsPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { role } = useAuth();
  const isAdmin = role === "admin";

  // Integrations hold a credential — admin-only surface.
  const visibleTabs = TABS.filter((t) => t.value !== "integrations" || isAdmin);
  const activeTab: TabValue = tab === "integrations" && !isAdmin ? "overview" : (tab as TabValue);

  const renderTab = () => {
    switch (activeTab) {
      case "overview":
        return <AdminOverview />;
      case "departments":
        return <DepartmentsPage />;
      case "users":
        return <UsersPage />;
      case "moderators":
        return <ModeratorsPage />;
      case "audit-log":
        return <AuditLogPage />;
      case "moderator-audit":
        return <ModeratorAuditPage />;
      case "activity-feed":
        return <TeamActivityPage />;
      case "ads":
        return <AdsSettingsPage />;
      case "integrations":
        return <IntegrationsSettingsPage />;
      case "color-theme":
        return <ColorThemePage />;
      default:
        return <AdminOverview />;
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-[22px] pt-4 pb-2">
        {visibleTabs.map((t) => {
          const active = t.value === activeTab;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() =>
                navigate({
                  to: "/admin/settings",
                  search: { tab: t.value } as never,
                })
              }
              className={cn(
                "h-8 rounded-full border px-4 text-[12px] font-medium transition-colors",
                active
                  ? "border-transparent bg-[#18181B] text-white"
                  : "border-[#E5E7EB] bg-white text-muted-foreground hover:bg-muted/50",
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {renderTab()}
    </>
  );
}
